// Mock data for GenMoney landing page

export const heroData = {
  headline: "Financial Wellbeing That Drives Engagement, Productivity, and Retention",
  subheadline: "Practical, scalable financial wellbeing programmes for UK employers and universities.",
  ctaPrimary: "Book a Free Strategy Call",
  ctaSecondary: "View Programmes & Pricing",
  calendlyLink: "https://calendly.com/hello-genmoney/30min"
};

export const problemData = {
  headline: "The Challenge Facing UK Organisations",
  problems: [
    {
      id: 1,
      title: "Financial stress reduces employee productivity and engagement",
      description: "Money worries impact focus, performance, and workplace wellbeing—costing organisations in lost productivity and increased absence."
    },
    {
      id: 2,
      title: "Students under financial pressure struggle academically and emotionally",
      description: "Financial anxiety affects student retention, mental health, and academic outcomes—undermining institutional goals."
    },
    {
      id: 3,
      title: "Many organisations lack structured, measurable financial wellbeing support",
      description: "Without a strategic approach, wellbeing initiatives fail to deliver ROI or align with broader people and inclusion strategies."
    }
  ]
};

export const solutionData = {
  headline: "GenMoney's Solution",
  subheadline: "Evidence-based financial wellbeing programmes that deliver measurable impact",
  capabilities: [
    "Weekly email financial wellbeing insights content",
    "Financial wellbeing campaigns & awareness weeks",
    "Personal finance campaigns",
    "Interactive webinars & expert workshops",
    "Financial wellbeing health surveys",
    "Quarterly financial health reports",
    "Expert-led masterclasses",
    "Financial wellbeing strategy (HR & ESG aligned)"
  ],
  description: "We don't just deliver content—we partner with you to embed financial wellbeing into your organisational culture, engagement, and retention strategy."
};

export const businessCaseData = {
  headline: "The Business Case for Financial Wellbeing",
  subheadline: "Strategic investment in your people delivers measurable returns",
  outcomes: [
    {
      id: 1,
      title: "Productivity",
      description: "Reduced financial stress means better focus, performance, and output across your workforce."
    },
    {
      id: 2,
      title: "Engagement",
      description: "Employees and students who feel supported are more engaged, motivated, and loyal."
    },
    {
      id: 3,
      title: "Retention",
      description: "Financial wellbeing programmes demonstrate genuine care—improving retention and reducing costly turnover."
    },
    {
      id: 4,
      title: "Inclusion & ESG",
      description: "Support financial inclusion and demonstrate tangible ESG commitment through accessible, impactful programmes."
    }
  ]
};

export const statsData = [
  { value: "85%", label: "Improved Financial Confidence" },
  { value: "10,000+", label: "Employees & Students Supported" },
  { value: "50+", label: "UK Organisations Trust Us" },
  { value: "4.8/5", label: "Average Programme Rating" }
];

export const solutionsData = [
  {
    id: 1,
    title: "For Employers",
    subtitle: "Strategic financial wellbeing for your workforce",
    description: "Partner with us to reduce financial stress, improve engagement, and demonstrate genuine commitment to your people's wellbeing.",
    benefits: [
      "Enhance employee wellbeing and mental health",
      "Boost retention and reduce absenteeism",
      "Support DEI and ESG commitments",
      "Flexible delivery: workshops, webinars, campaigns"
    ]
  },
  {
    id: 2,
    title: "For Universities",
    subtitle: "Support student success and wellbeing",
    description: "Equip students with practical financial skills that improve wellbeing, academic performance, and employability outcomes.",
    benefits: [
      "Improve student wellbeing and engagement",
      "Enhance employability and life skills",
      "Reduce dropout rates linked to money stress",
      "Scalable programmes for diverse cohorts"
    ]
  }
];

export const credibilityData = {
  headline: "Trusted by Leading UK Organisations & Universities",
  subheadline: "Delivered to thousands of employees and students across the UK"
};

export const featuresData = [
  {
    id: 1,
    title: "Evidence-Based Content",
    description: "Our programmes are grounded in behavioural science and financial psychology, delivering real, measurable outcomes."
  },
  {
    id: 2,
    title: "UK-Focused Expertise",
    description: "Tailored to UK financial systems, regulations, and the specific needs of British employers and students."
  },
  {
    id: 3,
    title: "Engaging Delivery",
    description: "Interactive workshops, webinars, and digital resources that resonate with diverse audiences and learning styles."
  },
  {
    id: 4,
    title: "Measurable Impact",
    description: "Track engagement, confidence gains, and behaviour change with our comprehensive reporting and analytics."
  },
  {
    id: 5,
    title: "Flexible Programmes",
    description: "From one-off sessions to year-long partnerships, we adapt to your organisation's needs and budget."
  },
  {
    id: 6,
    title: "Trusted Partner",
    description: "Experienced team with proven track record supporting wellbeing, HR, and student services teams across the UK."
  }
];

export const testimonialsData = [
  {
    id: 1,
    quote: "For some time I wanted to take my personal finances to the next level. Through the money mindset course with Genmoney, I now have a stronger more positive approach towards managing my money. Definitely recommend!",
    author: "Nadine Baker",
    role: "Analyst",
    company: "Financial Services"
  },
  {
    id: 2,
    quote: "The Money Mindset Session was very informative, engaging and practical, I got to learn about my relationship with my finances and practical ways to budget and manage my finances. I'd definitely recommend it!",
    author: "Nancy Quadri",
    role: "Analyst",
    company: "Cybersecurity"
  },
  {
    id: 3,
    quote: "I attended one of the Money Management Series hosted by GenMoney and it was excellent. The speakers were relatable, and they helped break down key money principles in a clear, practical way. I even discovered something new about my own money personality.",
    author: "Claudio Carmeli",
    role: "Software Engineer",
    company: "Tech"
  }
];

export const clientLogos = [
  {
    id: 1,
    name: "76 Agency",
    imageUrl: "https://static.wixstatic.com/media/40739e_5bf83c361b8c4ab0837f0c381147699e~mv2.jpg/v1/fill/w_150,h_150,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/76%20agency%20logo_edited.jpg"
  },
  {
    id: 2,
    name: "Queen Mary University of London",
    imageUrl: "https://static.wixstatic.com/media/40739e_bbf3c0ccaad94209bf4c0df67800107f~mv2.jpg/v1/fill/w_150,h_150,al_c,q_80,usm_0.66_1.00_0.01,enc_avif,quality_auto/qmul%20logo.jpg"
  },
  {
    id: 3,
    name: "Pinterest",
    imageUrl: "https://static.wixstatic.com/media/40739e_6344fa9fc8c94c2bae8c36f8ecd5330b~mv2.png/v1/fill/w_150,h_150,al_c,lg_1,q_85,enc_avif,quality_auto/pin%20logo.png"
  },
  {
    id: 4,
    name: "Warner Music Group",
    imageUrl: "https://static.wixstatic.com/media/40739e_e143dd981ea347c3be4c7c5a1695f362~mv2.png/v1/fill/w_150,h_150,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/Warner%20Music%20Logo.png"
  },
  {
    id: 5,
    name: "Springpod",
    imageUrl: "https://static.wixstatic.com/media/40739e_695c52abed9944a5a15289baf94bba01~mv2.png/v1/fill/w_150,h_150,al_c,q_85,usm_0.66_1.00_0.01,enc_avif,quality_auto/springpod%20logo.png"
  }
];

export const faqData = [
  {
    id: 1,
    question: "What topics do your programmes cover?",
    answer: "We cover practical financial topics including budgeting, saving, debt management, building financial resilience, money milestones and planning for the future, introduction to investing and building long term healthy money habits. All content is tailored to your audience's needs and life stage."
  },
  {
    id: 2,
    question: "How are the programmes delivered?",
    answer: "4 interactive webinars or workshops per year, one per quarter. Each lasts 45-60 minutes and is delivered online (in-person by arrangement, with travel at cost)"
  },
  {
    id: 3,
    question: "What makes GenMoney different from other financial education providers?",
    answer: "We focus specifically on financial wellbeing and behaviour change, not product sales. Our content is evidence-based, UK-focused, and designed to reduce financial stress while building practical skills. We measure impact, not just attendance."
  },
  {
    id: 4,
    question: "How do you measure the success of your programmes?",
    answer: "We use pre- and post-programme surveys to measure confidence gains, behaviour change intentions, and engagement. You'll receive detailed reports showing the impact on your employees or students, including wellbeing metrics."
  },
  {
    id: 5,
    question: "What size organisations do you work with?",
    answer: "We partner with mid-sized to large UK employers (typically 100+ employees) and universities of all sizes. Our programmes scale to meet your needs, from pilot programmes to organisation-wide rollouts."
  },
  {
    id: 6,
    question: "Is GenMoney authorised by the FCA?",
    answer: "We are an independent and unbiased financial education company and are not authorised by the Financial Conduct Authority (FCA) to provide financial advice. Neither will we sell specific products or services to you or your people."
  },
  {
    id: 7,
    question: "How do I get started?",
    answer: "Book a free consultation call with our team. We'll discuss your organisation's needs, explore programme options, and create a tailored proposal. No obligation, just expert guidance."
  }
];

export const pricingData = [
  {
    id: 1,
    name: "Foundation Financial Wellbeing",
    subtitle: "Perfect for organisations piloting financial wellbeing",
    price: "£2,500 – £3,500 per year",
    whoItsFor: "Organisations testing financial wellbeing impact before wider rollout",
    description: "Establish baseline engagement and awareness with structured, evidence-based support.",
    features: [
      "Weekly financial wellbeing email content",
      "4 interactive webinars or workshops per year (quarterly)",
      "Core financial wellbeing content library",
      "2 wellbeing awareness or personal finance campaigns"
    ],
    outcomes: "Establish baseline engagement and awareness.",
    cta: "Start with Foundation",
    popular: false,
    isBasePackage: true
  },
  {
    id: 2,
    name: "Growth & Engagement Programme",
    subtitle: "For mid-sized employers and universities",
    price: "£6,500 – £9,500 per year",
    whoItsFor: "Organisations seeking measurable impact and sustained engagement",
    description: "Drive deeper engagement with data-led insights and consistent touchpoints throughout the year.",
    basePackage: "Foundation",
    features: [
      "2 masterclasses or expert workshops",
      "2 Financial wellbeing campaigns & awareness weeks",
      "Financial wellbeing health survey",
      "Quarterly financial health summary report",
      "Branded wellbeing content for internal communications"
    ],
    outcomes: "Measurable engagement and wellbeing improvements.",
    cta: "Explore the Growth Programme",
    popular: true,
    isBasePackage: false
  },
  {
    id: 3,
    name: "Strategic Financial Wellbeing Partnership",
    subtitle: "For larger organisations and universities",
    price: "From £12,000 per year",
    whoItsFor: "Organisations embedding financial wellbeing into culture, retention, and inclusion strategy",
    description: "A strategic partnership that aligns financial wellbeing with your broader people, ESG, and organisational goals.",
    basePackage: "Growth",
    features: [
      "Bespoke financial wellbeing strategy (HR & ESG aligned)",
      "Quarterly strategy & insights calls",
      "6–8 expert-led workshops or masterclasses annually",
      "Full annual financial wellbeing impact report",
      "Custom campaigns aligned to organisational priorities",
      "Priority support and dedicated programme manager"
    ],
    outcomes: "Embed financial wellbeing into culture, retention, and inclusion strategy.",
    cta: "Book a Strategic Call",
    popular: false,
    isBasePackage: false
  }
];

export const pricingAddOns = [
  {
    id: 1,
    name: "Additional 1-to-1 Coaching Hours",
    description: "Confidential financial coaching sessions for employees or students",
    price: "From £120 per hour"
  },
  {
    id: 2,
    name: "Bespoke Content Development",
    description: "Tailored workshops or resources addressing specific organisational challenges",
    price: "From £750 per workshop"
  },
  {
    id: 3,
    name: "Leadership & Manager Training",
    description: "Equip leaders to support financial wellbeing conversations in their teams",
    price: "From £500 per session"
  },
  {
    id: 4,
    name: "Financial Wellbeing Audits",
    description: "Comprehensive organisational needs assessment with recommendations",
    price: "From £1,500"
  }
];

export const leadMagnetData = {
  headline: "Free Download: 2026 Financial Wellbeing Programme Guide",
  description: "Discover how leading UK organisations are transforming employee and student wellbeing. Includes case studies, ROI data, and implementation frameworks.",
  benefits: [
    "Proven programme frameworks",
    "Real ROI data from UK organisations",
    "Implementation best practices",
    "Topic selection guide"
  ],
  buttonText: "Download Free Guide"
};

export const ctaData = {
  headline: "Ready to Transform Financial Wellbeing?",
  description: "Try before you commit. Book a free 45-minute financial wellbeing session plus an anonymous workforce money-stress snapshot. Free sessions are available to a limited number of organisations, subject to availability.",
  buttonText: "Book Your Free Consultation"
};